import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { PostBody, formatMediaDate, linkProps, localized, useMediaItems } from "../lib/media";

// One of our own posts, at /media/<slug>.
const MediaPost = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const p = t.media;
  const { items, loaded } = useMediaItems();
  const post = items.find((i) => i.kind === "post" && i.slug === slug);
  const title = post ? localized(post, "title", lang) : "";

  useEffect(() => {
    if (!title) return undefined;
    const prevTitle = document.title;
    document.title = `${title} · Mise`;
    return () => {
      document.title = prevTitle;
    };
  }, [title]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(p.post.copied);
    } catch {
      toast.error(window.location.href);
    }
  };

  const backLink = (
    <Link to="/media" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="media-post-back">
      <ArrowLeft size={15} />
      {p.post.back}
    </Link>
  );

  if (!post) {
    return (
      <div className="mise-container py-20 md:py-24 max-w-3xl" data-testid="page-media-post">
        {backLink}
        {loaded && <p className="mt-10 text-xl text-white/70" data-testid="media-post-missing">{p.post.notFound}</p>}
      </div>
    );
  }

  const more = items.filter((i) => i.kind === "post" && i.slug !== slug).slice(0, 2);
  const cta = localized(post, "cta_label", lang);
  const CtaArrow = /^https?:/.test(post.url || "") ? ArrowUpRight : ArrowRight;

  return (
    <article data-testid="page-media-post">
      <header className="border-b border-[#242424]">
        <div className="mise-container pt-6 md:pt-8 pb-12 md:pb-14 max-w-4xl">
          {backLink}
          <p className="mt-8 flex flex-wrap items-center gap-x-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E8490F]">
            <span>{p.fromMise}</span>
            <span className="text-white/35">· {formatMediaDate(post.date, lang)}</span>
            {post.tag && <span className="text-white/35">· {p.tags[post.tag]}</span>}
          </p>
          <h1 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05] [text-wrap:balance]">{title}</h1>
          <p className="mt-6 mise-serif text-2xl md:text-[1.75rem] leading-snug text-white/85">{localized(post, "summary", lang)}</p>
        </div>
      </header>

      <div className="mise-container max-w-4xl py-12 md:py-16">
        {post.image && (
          <figure className="mb-12">
            <div className="overflow-hidden rounded-2xl border border-[#444444] bg-[#0d0d0d]">
              <img src={post.image} alt={post.image_alt || ""} className="block w-full h-auto" />
            </div>
          </figure>
        )}
        <div className="max-w-3xl">
          <PostBody text={localized(post, "body", lang)} />

          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-[#242424] pt-8">
            {post.url && cta && (
              <a {...linkProps(post.url)} className="mise-pill-primary" data-testid="media-post-cta" data-track={`media-post-cta-${post.slug}`}>
                {cta}
                <CtaArrow size={16} />
              </a>
            )}
            <button type="button" onClick={copyLink} className="mise-pill-secondary" data-testid="media-post-share">
              <Link2 size={16} />
              {p.post.share}
            </button>
          </div>
        </div>

        {more.length > 0 && (
          <section className="mt-16 border-t border-[#242424] pt-10" data-testid="media-post-more">
            <p className="mise-eyebrow mb-6">{p.post.more}</p>
            <div className="grid gap-5 md:grid-cols-2">
              {more.map((m) => (
                <Link
                  key={m.id}
                  to={`/media/${m.slug}`}
                  className="group rounded-2xl border border-[#444444] bg-[#1a1a1a] p-6 transition-all hover:-translate-y-0.5 hover:border-[#6a6a6a]"
                >
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-white/40">{formatMediaDate(m.date, lang)}</p>
                  <p className="mt-2 mise-serif text-xl leading-snug text-white">{localized(m, "title", lang)}</p>
                  <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/70 group-hover:text-white">
                    {p.readPost}
                    <ArrowRight size={14} className="text-[#E8490F]" />
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
};

export default MediaPost;
